// Location helpers shared by field modules through the SDK: where the phone is, where a job
// is (geocoded once per phone, as v1 does), and the day's weather at the job (Open-Meteo,
// no key, no account). Everything degrades to null; nothing here blocks a screen.
export interface Pos { lat: number; lng: number; acc: number }

export function getPosition(timeoutMs = 10000): Promise<Pos | null> {
  return new Promise(resolve => {
    if (!('geolocation' in navigator)) return resolve(null);
    navigator.geolocation.getCurrentPosition(
      p => resolve({ lat: +p.coords.latitude.toFixed(6), lng: +p.coords.longitude.toFixed(6), acc: Math.round(p.coords.accuracy || 0) }),
      () => resolve(null),
      { enableHighAccuracy: true, timeout: timeoutMs, maximumAge: 60000 });
  });
}

export async function geocode(address: string | null | undefined): Promise<{ lat: number; lng: number } | null> {
  if (!address) return null;
  const KEY = 'tcGeoCache';
  let cache: Record<string, { lat: number; lng: number }> = {};
  try { cache = JSON.parse(localStorage.getItem(KEY) || '{}'); } catch { /* fine */ }
  if (cache[address]) return cache[address];
  try {
    const r = await fetch('https://nominatim.openstreetmap.org/search?format=json&limit=1&q=' + encodeURIComponent(address));
    const j = (await r.json()) as { lat: string; lon: string }[];
    if (!j?.[0]) return null;
    const out = { lat: +j[0].lat, lng: +j[0].lon };
    cache[address] = out;
    try { localStorage.setItem(KEY, JSON.stringify(cache)); } catch { /* fine */ }
    return out;
  } catch { return null; }
}

export interface DayWeather { hi: number; lo: number; precipIn: number; windMph: number; code: number; summary: string }

const WMO: Array<[number, string]> = [
  [0, 'Clear'], [1, 'Mostly clear'], [2, 'Partly cloudy'], [3, 'Overcast'], [45, 'Fog'], [48, 'Freezing fog'],
  [51, 'Light drizzle'], [53, 'Drizzle'], [55, 'Heavy drizzle'], [56, 'Freezing drizzle'], [57, 'Freezing drizzle'],
  [61, 'Light rain'], [63, 'Rain'], [65, 'Heavy rain'], [66, 'Freezing rain'], [67, 'Freezing rain'],
  [71, 'Light snow'], [73, 'Snow'], [75, 'Heavy snow'], [77, 'Snow grains'], [80, 'Rain showers'], [81, 'Rain showers'], [82, 'Violent rain showers'],
  [85, 'Snow showers'], [86, 'Heavy snow showers'], [95, 'Thunderstorm'], [96, 'Thunderstorm with hail'], [99, 'Thunderstorm with hail'],
];
export const weatherText = (code: number) => WMO.find(([c]) => c === code)?.[1] ?? 'Unknown';

/** The day's weather at a point. Past days come from the archive, today and tomorrow from the forecast. */
export async function weatherFor(lat: number, lng: number, isoDate: string): Promise<DayWeather | null> {
  try {
    const today = new Date(); today.setHours(0, 0, 0, 0);
    const d = new Date(isoDate + 'T12:00:00');
    const daysBack = Math.round((today.getTime() - d.getTime()) / 86400000);
    const base = daysBack > 5 ? 'https://archive-api.open-meteo.com/v1/archive' : 'https://api.open-meteo.com/v1/forecast';
    const q = `latitude=${lat}&longitude=${lng}&daily=temperature_2m_max,temperature_2m_min,precipitation_sum,wind_speed_10m_max,weather_code&temperature_unit=fahrenheit&wind_speed_unit=mph&precipitation_unit=inch&timezone=auto&start_date=${isoDate}&end_date=${isoDate}`;
    const r = await fetch(`${base}?${q}`);
    if (!r.ok) return null;
    const j = (await r.json()) as { daily?: { temperature_2m_max: number[]; temperature_2m_min: number[]; precipitation_sum: number[]; wind_speed_10m_max: number[]; weather_code: number[] } };
    const y = j.daily; if (!y || y.temperature_2m_max?.[0] == null) return null;
    const code = y.weather_code[0] ?? 0;
    const hi = Math.round(y.temperature_2m_max[0]), lo = Math.round(y.temperature_2m_min[0]);
    const precipIn = +(y.precipitation_sum[0] ?? 0).toFixed(2), windMph = Math.round(y.wind_speed_10m_max[0] ?? 0);
    const summary = `${lo}° to ${hi}°, ${weatherText(code).toLowerCase()}${precipIn >= 0.1 ? `, ${precipIn}" precipitation` : ''}${windMph >= 20 ? `, wind to ${windMph} mph` : ''}`;
    return { hi, lo, precipIn, windMph, code, summary };
  } catch { return null; }
}
